
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  SunIcon,
  MoonIcon,
  PlusIcon,
  XMarkIcon,
  UserCircleIcon,
  Squares2X2Icon,
  UsersIcon,
  BellIcon,
  Cog8ToothIcon,
  PhoneIcon,
} from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import LeadForm from "../Lead Page/LeadForm";
import TaskForm from "../Task page/TaskForm";
import NoteForm from "../Note Page/NotePage";
import ReminderForm from "../Reminder Page/ReminderForm";
import MeetingForm from "../Meeting page/MeetingForm";
import { AuthContext } from "../context/AuthContext";
import { useIncomingMode } from "../context/IncomingModeContext";
import { API_URL } from "../../Constants";
import axios from "axios";
import IssueForm from "../Issues/IssuesForm";
import { FaBars } from "react-icons/fa";

export default function Navbar({
  onToggleSidebarMode,
  isSidebarOpen,
  onToggleSidebar,
}) {
  const { user } = useContext(AuthContext);
  const { isIncomingModeOn, toggleIncomingMode } = useIncomingMode();

  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [openForm, setOpenForm] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [sidebarMode, setSidebarMode] = useState("lead");
  
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedNotifications, setExpandedNotifications] = useState([]);
  const [popupNotification, setPopupNotification] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [userData, setUserData] = useState({});
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const profileDropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const addDropdownRef = useRef(null);
  const notificationStreamRef = useRef(null);
  const navigate = useNavigate();

  const isBusinessAccount = [
    "SUPER_SUPER_ADMIN",
    "SUPER_ADMIN",
    "RESELLER",
    "COMPANY",
  ].includes(user?.accountType);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const url =
          user?.accountType === "COMPANY" ||
          user?.accountType === "SUPER_ADMIN" ||
          user?.accountType === "RESELLER"
            ? `${API_URL}/api/user/details/${user.id}`
            : `${API_URL}/api/companyuser/details/${user.id}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const fetchedPermissions = response.data.permission?.[0] || {};
        setPermissions(fetchedPermissions);
        localStorage.setItem(
          "userPermissions",
          JSON.stringify(fetchedPermissions),
        );
      } catch (error) {
        console.error("Error fetching permissions:", error);
      } finally {
        setLoading(false);
      }
    };

    const savedPermissions = localStorage.getItem("userPermissions");
    if (savedPermissions) {
      setPermissions(JSON.parse(savedPermissions));
      setLoading(false);
    } else {
      fetchPermissions();
    }
  }, [user?.id, user?.accountType]);

  const hasPermission = (permissionKey) => {
    if (user?.accountType === "SUPER_SUPER_ADMIN") return true;
    return permissions[permissionKey] === true;
  };

  const getProfileImageUrl = useCallback(
    async (filename) => {
      if (!filename) {
        setProfileImage(null);
        return;
      }

      const endpoint = isBusinessAccount
        ? `${API_URL}/api/user/profileImage/${filename}`
        : `${API_URL}/api/companyuser/profileImage/${filename}`;

      try {
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        });

        const imageUrl = URL.createObjectURL(response.data);
        setProfileImage(imageUrl);
        localStorage.setItem("profileImage", imageUrl);
      } catch (error) {
        console.error("Error loading profile image:", error);
        setProfileImage(null);
      }
    },
    [isBusinessAccount],
  );

  const fetchUserData = useCallback(async () => {
    if (!user) return;

    const endpoint = isBusinessAccount
      ? `${API_URL}/api/user/details/${user.id}`
      : `${API_URL}/api/companyuser/details/${user.id}`;

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = response.data;
      setUserData(data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [user, isBusinessAccount]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (userData.profileImage) {
      getProfileImageUrl(userData.profileImage);
    }
  }, [userData.profileImage, getProfileImageUrl]);

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const handleSidebarModeToggle = () => {
    const newMode = sidebarMode === "account" ? "lead" : "account";
    setSidebarMode(newMode);
    onToggleSidebarMode(newMode);

    navigate(newMode === "account" ? "/viewAccount" : "/dashboard");
  };

  useEffect(() => {
    if (window.location.pathname === "/viewAccount") {
      setSidebarMode("account");
      onToggleSidebarMode("account");
    } else if (window.location.pathname === "/dashboard") {
      setSidebarMode("lead");
      onToggleSidebarMode("lead");
    }
  }, [onToggleSidebarMode]);

  const forms = {
    Lead: {
      component: (
        <LeadForm
          setOpenForm={setOpenForm}
          onSuccess={() => {
            window.dispatchEvent(new Event("leadCreated"));
          }}
          onClose={() => setOpenForm(null)}
        />
      ),
      permissionKey: "leads",
    },
    Task: {
      component: <TaskForm setOpenForm={setOpenForm} />,
      permissionKey: "task",
    },
    Issue: {
      component: <IssueForm setOpenForm={setOpenForm} />,
      permissionKey: "issues",
    },
    Note: {
      component: (
        <NoteForm
          setOpenForm={setOpenForm}
          onSave={() => {
            window.dispatchEvent(new Event("noteCreated"));
          }}
          onClose={() => setOpenForm(null)}
          user={user}
          companyId={
            user?.accountType === "COMPANY" ? user?.id : user?.companyId
          }
        />
      ),
      permissionKey: "notes",
    },
    Reminder: {
      component: <ReminderForm setOpenForm={setOpenForm} />,
      permissionKey: "reminder",
    },
    Meeting: {
      component: <MeetingForm setOpenForm={setOpenForm} />,
      permissionKey: "meeting",
    },
  };

  const filteredForms = Object.entries(forms).reduce((acc, [key, value]) => {
    if (!value.permissionKey || hasPermission(value.permissionKey)) {
      acc[key] = value;
    }
    return acc;
  }, {});

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/notifications/user/${user?.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();

      setNotifications(data.length > 0 ? data : []);
      const unread = data.filter((n) => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user?.id]);

  // Live notification stream (SSE) — pushes new notifications the instant
  // they're created (meeting assigned, reminder, 5-minute popup, etc.)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!user?.id || !token) return;

    const es = new EventSource(
      `${API_URL}/api/notifications/stream/${user.id}?token=${encodeURIComponent(
        token,
      )}`,
    );
    notificationStreamRef.current = es;

    es.addEventListener("connected", () => {
      console.log("Notification stream connected");
    });

    es.addEventListener("notification", (event) => {
      try {
        const notification = JSON.parse(event.data);
        setNotifications((prev) => [notification, ...prev]);
        if (!notification.read) {
          setUnreadCount((prev) => prev + 1);
        }
        if (notification.type === "MEETING_POPUP") {
          setPopupNotification(notification);
        }
      } catch (err) {
        console.error("Failed to parse notification stream event:", err);
      }
    });

    es.onerror = () => {
      // native EventSource auto-reconnects; nothing to do here
    };

    return () => {
      es.close();
      notificationStreamRef.current = null;
    };
  }, [user?.id]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
      );
      fetchNotifications();
    } catch (err) {
      console.error("Failed to mark as read", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/notifications/markAllAsRead/${user?.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchNotifications();
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read", err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setShowProfileDropdown(false);
      }
      if (
        addDropdownRef.current &&
        !addDropdownRef.current.contains(event.target)
      ) {
        setShowAddDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleExpanded = (id) => {
    setExpandedNotifications((prev) =>
      prev.includes(id) ? prev.filter((nid) => nid !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" ||
        (event.altKey && event.key.toLowerCase() === "c")
      ) {
        setOpenForm(null);
      }
    };

    if (openForm) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openForm]);

  useEffect(() => {
    const savedImage = localStorage.getItem("profileImage");
    if (savedImage) {
      setProfileImage(savedImage);
    }
  }, []);

  return (
    <>
      {/* Inline styles for smooth opening animations */}
      <style>{`
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-dropdown {
          animation: dropdownFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      <div className={openForm ? "blur-sm pointer-events-none" : ""}>
        {/* ULTRA-SLIM Navbar: Height set to h-12 (48px) */}
<nav className="sticky top-0 z-50 flex w-full h-12 items-center justify-between px-2 md:px-4 bg-gradient-to-r from-indigo-100 via-white to-purple-100 dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800 transition-all duration-200">          {/* LEFT SIDE: Menu Toggle + Search Bar + Add Dropdown */}
          <div className="flex flex-1 min-w-0 items-center gap-1 md:gap-3">
            
            {/* Sidebar Toggle (Slimmer: w-7 h-7) */}
            <button
              onClick={() => onToggleSidebar(!isSidebarOpen)}
              aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
              className="flex items-center justify-center shrink-0 w-10 h-10 md:w-7 md:h-7 rounded-xl md:rounded-full bg-white/80 md:bg-transparent border border-indigo-100 md:border-0 shadow-sm md:shadow-none text-[#673AB7] md:text-gray-500 dark:text-gray-400 hover:text-[#0C4550] hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition-colors"
            >
              {isSidebarOpen ? (
                <XMarkIcon className="w-5 h-5 md:w-4 md:h-4" />
              ) : (
                <FaBars className="w-4 h-4 md:w-3.5 md:h-3.5" />
              )}
            </button>

            {/* Ultra-Slim Search Bar (h-8, smaller text and icons) */}
            <button
              onClick={() => window.dispatchEvent(new Event("open-command-menu"))}
              className="flex items-center justify-center md:justify-between shrink-0 md:shrink w-10 h-10 md:w-full md:max-w-[200px] lg:max-w-[400px] md:h-8 px-0 md:px-3 text-[13px] text-gray-500 bg-white dark:bg-gray-800 border border-gray-300 focus:outline-none hover:border-gray-300 dark:hover:border-gray-600 rounded-full transition-all duration-200 cursor-text"
              title="Search (Ctrl + Space)"
            >
              <div className="flex items-center gap-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 md:w-[14px] md:h-[14px] text-gray-500 md:text-gray-400"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
                <span className="hidden md:inline font-medium text-gray-500 dark:text-gray-400 mt-[1px]">Find or Ask...</span>
              </div>
              <kbd className="hidden sm:inline-block px-1.5 py-[1px] text-[9px] font-semibold text-gray-500 dark:text-gray-500 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                Ctrl Space
              </kbd>
            </button>

            {/* Slim Plus (+) Dropdown (w-7 h-7) - Reverted to Original */}
            {!loading && sidebarMode !== "account" && (
              <div className="relative" ref={addDropdownRef}>
                <button
                  onClick={() => setShowAddDropdown(!showAddDropdown)}
                  className="flex items-center justify-center shrink-0 w-10 h-10 md:w-7 md:h-7 bg-[#673AB7] text-white dark:text-gray-300  dark:border-gray-600 rounded-full transition-colors focus:outline-none"
                  title="Create New"
                >
                  <PlusIcon className="w-5 h-5 md:w-4 md:h-4" />
                </button>

                {showAddDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-black/80 dark:bg-gray-800 shadow-lg rounded-md border border-gray-100 dark:border-gray-700 py-1 z-50 overflow-hidden">
                    {Object.entries(filteredForms).map(
                      ([formName, formConfig]) => (
                        <button
                          key={formName}
                          onClick={() => {
                            setOpenForm(formName);
                            setShowAddDropdown(false);
                          }}
                          className="w-full flex items-center px-4 py-3 md:py-1.5 text-[16px] text-left text-white dark:text-gray-200 hover:bg-[#EEF2FC] dark:hover:bg-gray-700 hover:text-black dark:hover:text-white transition-colors"
                        >
                          <PlusIcon className="w-3.5 h-3.5 mr-2 text-gray-400" />
                          {formName}
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Incoming Mode: Call icon + slider (Universal across the app) */}
            {!loading && sidebarMode !== "account" && (
              <div className="flex shrink-0 items-center gap-1.5 pl-1" title="Incoming Mode">
                <PhoneIcon
                  className={`w-4 h-4 transition-colors ${
                    isIncomingModeOn
                      ? "text-[#0C4550] dark:text-emerald-400"
                      : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                <button
                  type="button"
                  role="switch"
                  aria-checked={isIncomingModeOn}
                  onClick={toggleIncomingMode}
                  title={
                    isIncomingModeOn ? "Incoming Mode: ON" : "Incoming Mode: OFF"
                  }
                  className={`relative inline-flex shrink-0 h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none before:absolute before:-inset-2.5 md:before:hidden ${
                    isIncomingModeOn
                      ? "bg-[#0C4550]"
                      : "bg-gray-300 dark:bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                      isIncomingModeOn ? "translate-x-[18px]" : "translate-x-[3px]"
                    }`}
                  />
                </button>
              </div>
            )}
          </div>

          {/* RIGHT SIDE: Action Icons & Profile */}
          <div className="flex items-center gap-1 ml-auto">
            
            {/* Account Mode Toggle (w-7 h-7) */}
            {user?.accountType !== "EXECUTIVE" && (
              <button
                onClick={handleSidebarModeToggle}
                className="flex items-center justify-center w-10 h-10 md:w-7 md:h-7 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title={`Switch to ${
                  sidebarMode === "account"
                    ? "Lead Management"
                    : "Account Management"
                }`}
              >
                {sidebarMode === "account" ? (
                  <UsersIcon className="w-6 h-6 md:w-[18px] md:h-[18px]" />
                ) : (
                  <Squares2X2Icon className="w-6 h-6 md:w-[18px] md:h-[18px]" />
                )}
              </button>
            )}

            {/* Notifications (w-7 h-7) */}
            <div className="relative flex items-center" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative flex items-center justify-center w-10 h-10 md:w-7 md:h-7 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Notifications"
              >
                <BellIcon className="w-6 h-6 md:w-[18px] md:h-[18px]" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4 bg-[#F46653] text-white text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center border-[1.5px] border-white dark:border-gray-900 leading-none">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="fixed left-2 right-2 top-[54px] md:absolute md:left-auto md:right-0 md:top-[calc(100%+10px)] z-50 w-auto md:w-80 md:max-w-sm shadow-xl rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 overflow-hidden origin-top-right animate-dropdown">
                  <div className="sticky top-0 z-10 flex justify-between items-center px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/90 backdrop-blur-sm">
                    <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                      Notifications
                    </span>
                    <button
                      onClick={markAllAsRead}
                      className="text-xs md:text-[11px] py-1 font-medium text-[#0C4550] hover:text-[#09323A] dark:text-blue-400"
                    >
                      Mark all as read
                    </button>
                  </div>

                  <div className="max-h-[65vh] md:max-h-80 overflow-y-auto">
                    {notifications?.length === 0 ? (
                      <div className="p-5 text-center">
                        <p className="text-[13px] text-gray-500 dark:text-gray-400">
                          No new notifications
                        </p>
                      </div>
                    ) : (
                      notifications?.map((n) => (
                        <div
                          key={n.notificationId}
                          onClick={() => markAsRead(n.notificationId)}
                          className={`cursor-pointer p-4 md:p-3 border-b dark:border-gray-700 transition duration-150 ${
                            n.read
                              ? "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                              : "bg-[#EEF2FC]/50 dark:bg-gray-700/50 hover:bg-[#EEF2FC] dark:hover:bg-gray-700"
                          }`}
                        >
                          <div className="flex flex-col">
                            <p
                              className={`text-[13px] text-gray-800 dark:text-gray-100 leading-relaxed ${
                                !n.read ? "font-semibold" : ""
                              }`}
                            >
                              {expandedNotifications.includes(n.notificationId)
                                ? n.message
                                : n.message.length > 100
                                ? `${n.message.substring(0, 100)}...`
                                : n.message}
                            </p>
                            {n.message.length > 100 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpanded(n.notificationId);
                                }}
                                className="mt-1 self-start text-[11px] font-medium text-[#0C4550] dark:text-blue-400"
                              >
                                {expandedNotifications.includes(n.notificationId)
                                  ? "Show less"
                                  : "Read more"}
                              </button>
                            )}
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                              {new Date(n.createdAt).toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Dark Mode (w-7 h-7) */}
            {/* <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="flex items-center justify-center w-10 h-10 md:w-7 md:h-7 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Toggle Theme"
            >
              {theme === "dark" ? (
                <SunIcon className="w-[18px] h-[18px] text-yellow-400" />
              ) : (
                <MoonIcon className="w-[18px] h-[18px]" />
              )}
            </button> */}

            {/* Settings (w-7 h-7) */}
            {/* <button
              onClick={() => navigate("/settings")}
              className="flex items-center justify-center w-10 h-10 md:w-7 md:h-7 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mr-1"
              title="Settings"
            >
              <Cog8ToothIcon className="w-[18px] h-[18px]" />
            </button> */}

            {/* Vertical Divider */}
            <div className="hidden sm:block h-5 w-px bg-gray-300 dark:bg-gray-700 mx-1"></div>

            {/* Profile Dropdown */}
            <div className="relative flex items-center ml-1" ref={profileDropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="w-10 h-10 md:w-7 md:h-7 shrink-0 rounded-full overflow-hidden border border-gray-300 dark:border-gray-600 hover:ring-2 hover:ring-gray-400 dark:hover:ring-gray-500 transition-all focus:outline-none"
                title="Profile"
              >
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserCircleIcon className="w-full h-full text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800" />
                )}
              </button>

              {showProfileDropdown && (
                <div className="absolute right-0 top-[calc(100%+12px)] w-64 bg-white dark:bg-gray-800 shadow-[0_20px_50px_rgba(0,0,0,0.15)] rounded-2xl overflow-hidden z-50 border border-gray-100 dark:border-gray-700 origin-top-right animate-dropdown">
                  <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full border-2 border-[#EEF2FC] overflow-hidden shadow-sm flex-shrink-0">
                        {profileImage ? (
                          <img
                            src={profileImage}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <UserCircleIcon className="w-full h-full text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800" />
                        )}
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-[14px] font-bold text-[#0C4550] dark:text-white leading-tight truncate">
                          {user?.name || "Account User"}
                        </span>
                        <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          {user?.email || "user@example.com"}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        navigate("/profile");
                      }}
                      className="w-full flex items-center px-3 py-3.5 md:py-2.5 text-[13px] font-medium text-gray-700 dark:text-gray-200 rounded-xl hover:bg-[#EEF2FC] hover:text-[#0C4550] dark:hover:bg-gray-700 transition-all"
                    >
                      <Cog8ToothIcon className="w-4 h-4 mr-2.5 text-gray-400" />
                      Profile Settings
                    </button>
                    
                    <button
                      onClick={async () => {
                        setShowProfileDropdown(false);
                        try {
                          await fetch(
                            `${API_URL}/api/session/manualLogout/${user?.id}`,
                            {
                              method: "POST",
                              headers: {
                                Authorization: `Bearer ${localStorage.getItem("token")}`,
                                "Content-Type": "application/json",
                              },
                            },
                          );
                        } catch (error) {
                          console.error("Logout API failed:", error);
                        }

                        localStorage.removeItem("token");
                        localStorage.removeItem("userPermissions");
                        localStorage.removeItem("sidebarDropdownStates");
                        localStorage.removeItem("profileImage");
                        localStorage.setItem("logout", Date.now());
                        navigate("/");
                      }}
                      className="w-full flex items-center px-3 py-3.5 md:py-2.5 mt-1 text-[13px] font-medium text-red-600 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 mr-2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </nav>
      </div>

      {/* Meeting starting-soon popup (from SSE MEETING_POPUP notifications) */}
      {popupNotification && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center w-12 h-12 rounded-full bg-[#F46653]/10">
              <BellIcon className="w-6 h-6 text-[#F46653]" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
              Meeting Starting Soon
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              {popupNotification.message}
            </p>
            <button
              onClick={() => setPopupNotification(null)}
              className="px-6 py-3 md:px-4 md:py-2 bg-[#673AB7] text-white rounded-lg hover:bg-[#5a2fa0] transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* MODALS RENDER */}
      {openForm && filteredForms[openForm] && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex justify-center items-end md:items-center z-50 p-0 md:p-4 transition-opacity duration-300">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] md:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 flex justify-between items-center border-b border-gray-100 dark:border-gray-700 px-5 py-3">
              <h2 className="text-[15px] font-semibold text-gray-800 dark:text-white tracking-tight">
                Create {openForm}
              </h2>
              <button
                onClick={() => setOpenForm(null)}
                className="p-2 md:p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 rounded-full transition-colors"
                aria-label="Close Modal"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-3 py-4 md:px-6 md:py-5 overflow-y-auto bg-gray-50 dark:bg-gray-900/50">
              {filteredForms[openForm].component}
            </div>
          </div>
        </div>
      )}
    </>
  );
}